/**
 * PrimeiroUsoWizard — Assistente de primeiro uso
 *
 * 4 etapas:
 *   1. Escolher área (categoria do template)
 *   2. Participantes (selecionar usuários e funções)
 *   3. Escolher modelo (template pré-configurado)
 *   4. Executar (cria tudo automaticamente)
 *
 * Rota: /primeiro-uso
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  DollarSign,
  ShoppingCart,
  Users,
  Settings,
  Puzzle,
  Rocket,
  Clock,
  Shield,
  Play,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import {
  PROCESS_TEMPLATES,
  CATEGORY_LABELS,
  getTemplatesByCategory,
  getTemplateRoles,
  type ProcessTemplate,
  type ProcessTemplateCategory,
} from '../data/processTemplates';
import {
  executeFirstUse,
  listOrgMembers,
  type RoleAssignment,
  type FirstUseResult,
} from '../services/firstUseService';

// ── Constants ─────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Área' },
  { id: 2, label: 'Participantes' },
  { id: 3, label: 'Modelo' },
  { id: 4, label: 'Iniciar' },
];

const CATEGORY_OPTIONS: { id: ProcessTemplateCategory; label: string; icon: React.ReactNode; description: string }[] = [
  { id: 'financeiro', label: 'Financeiro', icon: <DollarSign size={24} />, description: 'Contas a pagar, aprovações, pagamentos' },
  { id: 'compras', label: 'Compras', icon: <ShoppingCart size={24} />, description: 'Solicitações, cotações, pedidos' },
  { id: 'rh', label: 'RH', icon: <Users size={24} />, description: 'Admissão, documentos, acessos' },
  { id: 'operacional', label: 'Operacional', icon: <Settings size={24} />, description: 'Processos genéricos de produção' },
  { id: 'personalizado', label: 'Personalizado', icon: <Puzzle size={24} />, description: 'Crie seu próprio fluxo' },
];

// ── Wizard Component ──────────────────────────────────────

export const PrimeiroUsoWizard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const userId = user?.id;
  const userOrganizationId = user?.organization_id ?? user?.organizationId;
  const organizationId = workspace?.organizationId ?? userOrganizationId;

  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<ProcessTemplateCategory | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ProcessTemplate | null>(null);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const [result, setResult] = useState<FirstUseResult | null>(null);

  // Fetch org members for step 2
  const membersQuery = useQuery({
    queryKey: ['org-members', organizationId],
    queryFn: () => listOrgMembers(organizationId!),
    enabled: Boolean(organizationId),
  });

  // Available templates for selected category
  const categoryTemplates = useMemo(() => {
    if (!selectedCategory) return [];
    if (selectedCategory === 'personalizado') {
      return PROCESS_TEMPLATES.filter((t) => t.category === 'operacional');
    }
    return getTemplatesByCategory(selectedCategory);
  }, [selectedCategory]);

  // Auto-select first template when category changes
  useEffect(() => {
    if (categoryTemplates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(categoryTemplates[0]);
    }
  }, [categoryTemplates, selectedTemplate]);

  // Build default role assignments when template changes
  useEffect(() => {
    if (!selectedTemplate || !userId) return;
    const roles = getTemplateRoles(selectedTemplate);
    const members = membersQuery.data ?? [];

    const assignments: RoleAssignment[] = roles.map((role) => ({
      role,
      userId: members[0]?.userId ?? userId,
      userName: members[0]?.name ?? user?.name ?? '',
    }));

    setRoleAssignments(assignments);
  }, [selectedTemplate, membersQuery.data, userId, user?.name]);

  // Execute mutation
  const executeMutation = useMutation({
    mutationFn: () => {
      if (!selectedTemplate || !organizationId || !userId) {
        return Promise.reject(new Error('Dados incompletos'));
      }
      return executeFirstUse({
        template: selectedTemplate,
        organizationId,
        userId,
        title: `${selectedTemplate.name} — Primeira execução`,
        roleAssignments,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setStep(4);
    },
  });

  const handleNext = useCallback(() => {
    if (step === 3) {
      executeMutation.mutate();
      return;
    }
    setStep((s) => Math.min(s + 1, 4));
  }, [step, executeMutation]);

  const handleBack = useCallback(() => {
    setStep((s) => Math.max(s - 1, 1));
  }, []);

  const handleGoToMinhaMesa = useCallback(() => {
    navigate('/minha-mesa');
  }, [navigate]);

  const handleSkip = useCallback(() => {
    navigate('/centro-operacional');
  }, [navigate]);

  const canNext = useMemo(() => {
    if (step === 1) return Boolean(selectedCategory);
    if (step === 2) return roleAssignments.length > 0;
    if (step === 3) return Boolean(selectedTemplate) && !executeMutation.isPending;
    return false;
  }, [step, selectedCategory, roleAssignments, selectedTemplate, executeMutation.isPending]);

  const updateRoleUser = useCallback((role: string, newUserId: string) => {
    const members = membersQuery.data ?? [];
    const member = members.find((m) => m.userId === newUserId);
    setRoleAssignments((prev) =>
      prev.map((a) => (a.role === role ? { ...a, userId: newUserId, userName: member?.name ?? '' } : a)),
    );
  }, [membersQuery.data]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-[#111111]">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-sm bg-slate-200 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:bg-slate-800 dark:text-slate-400">
            <Rocket size={14} />
            Primeiro uso
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Configure seu primeiro processo</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Em poucos passos, sua equipe estará operando.</p>
        </div>

        {/* Progress */}
        <div className="mb-6 flex items-center justify-center gap-1">
          {STEPS.map((s) => (
            <div key={s.id} className="flex items-center gap-1">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition ${
                  step >= s.id
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'bg-slate-200 text-slate-400 dark:bg-slate-800 dark:text-slate-600'
                }`}
              >
                {step > s.id ? <CheckCircle2 size={14} /> : s.id}
              </div>
              <span className={`mr-3 text-xs font-medium ${step >= s.id ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400 dark:text-slate-600'}`}>
                {s.label}
              </span>
              {s.id < STEPS.length && <div className={`h-px w-6 ${step > s.id ? 'bg-slate-900 dark:bg-slate-300' : 'bg-slate-200 dark:bg-slate-800'}`} />}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="rounded border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-[#1a1a1a]">
          {/* Step 1: Choose category */}
          {step === 1 && (
            <div>
              <h2 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">O que você quer organizar?</h2>
              <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">Escolha a área e carregaremos modelos prontos para você.</p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {CATEGORY_OPTIONS.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => { setSelectedCategory(cat.id); setSelectedTemplate(null); }}
                    className={`flex items-start gap-3 rounded-md border p-4 text-left transition ${
                      selectedCategory === cat.id
                        ? 'border-slate-900 bg-slate-50 dark:border-slate-300 dark:bg-slate-900/50'
                        : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className={`mt-0.5 ${selectedCategory === cat.id ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}`}>
                      {cat.icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{cat.label}</p>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{cat.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Participants */}
          {step === 2 && selectedTemplate && (
            <div>
              <h2 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">Quem participa?</h2>
              <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
                Associe cada função a um membro da equipe. As funções serão criadas automaticamente.
              </p>

              {membersQuery.isLoading ? (
                <div className="flex items-center justify-center py-8 text-sm text-slate-400">
                  <Loader2 size={16} className="mr-2 animate-spin" /> Carregando equipe...
                </div>
              ) : (
                <div className="space-y-4">
                  {roleAssignments.map((assignment) => (
                    <div key={assignment.role} className="flex items-center gap-3">
                      <div className="flex w-32 shrink-0 items-center gap-2">
                        <Shield size={14} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{assignment.role}</span>
                      </div>
                      <select
                        value={assignment.userId}
                        onChange={(e) => updateRoleUser(assignment.role, e.target.value)}
                        className="flex-1 rounded border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      >
                        {(membersQuery.data ?? []).map((member) => (
                          <option key={member.userId} value={member.userId}>
                            {member.name} ({member.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Choose template */}
          {step === 3 && (
            <div>
              <h2 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">Escolha um modelo</h2>
              <p className="mb-5 text-sm text-slate-500 dark:text-slate-400">
                Cada modelo já vem com etapas, funções e prazos configurados.
              </p>

              <div className="space-y-3">
                {categoryTemplates.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setSelectedTemplate(tpl)}
                    className={`w-full rounded-md border p-4 text-left transition ${
                      selectedTemplate?.id === tpl.id
                        ? 'border-slate-900 bg-slate-50 dark:border-slate-300 dark:bg-slate-900/50'
                        : 'border-slate-200 hover:border-slate-300 dark:border-slate-700 dark:hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{tpl.name}</p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{tpl.description}</p>
                      </div>
                      <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">
                        {tpl.stages.length} etapas
                      </span>
                    </div>
                    {selectedTemplate?.id === tpl.id && (
                      <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 dark:border-slate-800">
                        {tpl.stages.map((stage, i) => (
                          <div key={i} className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-500">
                              {i + 1}
                            </span>
                            <span className="flex-1">{stage.name}</span>
                            <span className="flex items-center gap-1 text-slate-400">
                              <Shield size={10} /> {stage.role}
                            </span>
                            <span className="flex items-center gap-1 text-slate-400">
                              <Clock size={10} /> {stage.slaHours}h
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 4 && result && (
            <div className="py-4 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                <CheckCircle2 size={32} className="text-emerald-600 dark:text-emerald-300" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Sua primeira execução está pronta!</h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Foram criadas <span className="font-semibold text-slate-700 dark:text-slate-200">{result.tasksCreated} etapas</span> no processo{' '}
                <span className="font-mono text-xs text-slate-600 dark:text-slate-300">{result.referenceCode}</span>.
              </p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Acesse sua mesa de trabalho para começar.
              </p>

              <button
                type="button"
                onClick={handleGoToMinhaMesa}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              >
                <Play size={14} />
                Ir para Minhas Tarefas
              </button>
            </div>
          )}

          {/* Step 4 loading */}
          {step === 4 && !result && executeMutation.isPending && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="mb-3 animate-spin text-slate-400" />
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Criando processo, funções e tarefas...</p>
            </div>
          )}

          {/* Error state */}
          {executeMutation.isError && (
            <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
              Erro ao criar execução: {(executeMutation.error as Error)?.message ?? 'Tente novamente.'}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        {step < 4 && (
          <div className="mt-4 flex items-center justify-between">
            <div>
              {step > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-1 text-sm text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                >
                  <ArrowLeft size={14} /> Voltar
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSkip}
                className="text-sm text-slate-400 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              >
                Pular
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={!canNext}
                className="flex items-center gap-2 rounded-md bg-slate-900 px-5 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
              >
                {executeMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : step === 3 ? (
                  <>Criar e executar <Rocket size={14} /></>
                ) : (
                  <>Próximo <ArrowRight size={14} /></>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
